import React, { useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'

import Button from '../../button'
import Loader from '../../loader'

import { useReadingLanguages } from '../../../../data/hooks/feed'
import { useDialog } from '../../dialog/hook'

type ReadingLanguagesDialogProps = {
  onUpdated?: (languages: string[]) => void
}

const supportedLanguages = [
    // "all",
    "ar",
    "cs",
    "da",
    "de",
    "el",
    "en",
    "es",
    "fi",
    "fr",
    "it",
    "ja",
    "nl",
    "no",
    "pl",
    "pt",
    "ro",
    "ru",
    "tr",
    "uk",
    "zh-cn",
    "zh-tw",
    "other",
]

type ActiveState = {
    [key: string]: boolean
}

const ReadingLanguagesDialog: React.FunctionComponent<ReadingLanguagesDialogProps> = ({ onUpdated }) => {
    const { t } = useTranslation("onboarding")
    const { closeDialog } = useDialog()

    const [userLanguages, updateUserLanguages] = useReadingLanguages()

    const [activeLanguages, setLanguageActive] = useReducer(
        (state: ActiveState, newState: Partial<ActiveState>): ActiveState => ({...state, ...newState} as ActiveState), 
        supportedLanguages.reduce((acc, el) => ({...acc, [el]: userLanguages.data?.includes(el)}), {})
    )

    useEffect(() => {
        setLanguageActive(supportedLanguages.reduce((acc, el) => ({...acc, [el]: userLanguages.data?.includes(el)}), {}))
    }, [userLanguages.data])

    return (
        <Container>
            <CloseContainer onClick={() => closeDialog()}>
                <Close src={require('./images/close.svg')}/>
            </CloseContainer>
            <Title>{t("reading_languages.title")}</Title>
            <Subtitle>{t("reading_languages.subtitle")}</Subtitle>
            <Languages>
                { userLanguages.pending && <Loader />}
                { !userLanguages.pending && supportedLanguages.map(lng => (
                    <Language active={activeLanguages[lng]} onClick={() => setLanguageActive({ [lng]: !activeLanguages[lng] })} key={lng}>
                        <Icon src={require(`./images/flags/${lng}.png`)}/>
                        <Name>{t(`reading_languages.languages.${lng}`)}</Name>
                    </Language>
                ))
                }
            </Languages>
            <Button 
                onClick={() => {
                    const newLanguages = Object.keys(activeLanguages).filter(lng => activeLanguages[lng]) 
                    updateUserLanguages(newLanguages)
                    onUpdated && onUpdated(newLanguages)
                }}
            >
                {t("reading_languages.apply")}
            </Button>
        </Container>
    )
}

const Container = styled.div`
    position: relative;
  
    display: grid;
    align-items: start;

    grid-template-columns: 1fr;
    grid-row-gap: 24px;
    grid-column-gap: 24px;

    width: 100%;
    height: 100%;

    padding: 24px;

    background: ${p => p.theme.colors.bg};
    border-radius: 10px;

    ${up('desktop')} {
        width: 1024px;
        height: fit-content;
        grid-column-gap: 48px;
    }

    overflow-y: hidden;
`

const CloseContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    
    height: 60px;
    width: 60px;

    display: grid;
    align-items: center;
    justify-items: center;

    cursor: pointer;
    z-index: 10;
`

const Close = styled.img`
    width: 20px;
    height: 20px;
`


const Title = styled.div`
    text-align: center;
    width: 100%;

    font-size: 16px;
    font-weight: bold;
`


const Subtitle = styled.div`
    text-align: center;
    width: 100%;
`

const Languages = styled.div`
    display: grid;
    justify-items: center;
    align-items: center;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 16px;

    cursor: pointer;

    ${up('desktop')} {
        grid-template-columns: repeat(4, 1fr);
        grid-gap: 32px;
    }
`

const Language = styled.div<{active: boolean}>`
    display: grid;
    align-items: center;
    justify-items: start;
    grid-template-columns: auto 1fr;
    grid-gap: 10px;

    padding: 16px;
    border-radius: 333px;
    width: 100%;

    background: ${p => p.active ? p.theme.colors.accent : p.theme.colors.lightGray};
    color: ${p => p.active ? p.theme.colors.bg : p.theme.colors.main};
`

const Icon = styled.img`
    height: 20px;
`

const Name = styled.div`
    font-size: 14px;
`

export default ReadingLanguagesDialog
